import React, {
  useCallback, useId, useRef, useState,
} from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LeadRecordSearchResults from './LeadRecordSearch';
import LeadSearchAdornment from './LeadSearchAdornment';
import { leadItemChipSx } from './LeadItemChips';
import { describeLeadRecordFilter } from '../leadRecordSearch';
import { LEADS_DANGER, LEADS_TEXT_MUTED, leadsSectionTitleSx, leadsTextFieldSx } from './leadsUi.styles';

const recordKey = (rec) => `${rec?.itemKind}:${rec?.id}`;

/**
 * Elegir registros propios (lote / UF / parcela CAD) con el buscador de
 * Leads (`/searchleadrecords`, todos los registros: `has_activity=false`).
 * Los elegidos quedan como chips con ✕. `max` 1 = selección simple
 * (asignar registro); la carga manual usa hasta 20. Resultados en lista
 * flotante sobre el diálogo (`LeadRecordSearchResults floating`), con ↑ / ↓ /
 * Enter / → / ←; Escape cierra la lista (no el diálogo); en multi queda
 * abierta tras elegir.
 *
 * @param {{ value: { itemKind: string, id: string, label: string }[],
 *   onChange: (next: object[]) => void, max?: number, ownerUserId?: string|null,
 *   disabled?: boolean, errorIndex?: number|null, error?: string,
 *   label?: string, hint?: string, autoFocus?: boolean, itemKinds?: string[]|null }} props
 *   `itemKinds`: solo esos tipos se buscan y se eligen (crear campaña: `user_lot`, `cad_parcel`).
 */
export default function LeadRecordPicker({
  value = [],
  onChange,
  max = 20,
  ownerUserId = null,
  disabled = false,
  errorIndex = null,
  error = '',
  label = 'Registros (opcional)',
  hint = '',
  autoFocus = false,
  itemKinds = null,
}) {
  const [text, setText] = useState('');
  // Lista flotante: cerrada a mano (Escape, salir del input); se reabre al escribir o enfocar.
  const [dismissed, setDismissed] = useState(false);
  const [activeOptionId, setActiveOptionId] = useState(null);
  const anchorRef = useRef(null);
  const resultsRef = useRef(null);
  const listId = useId();
  const list = Array.isArray(value) ? value : [];
  const full = list.length >= max;
  const open = !full && Boolean(text.trim()) && !dismissed && !disabled;
  const handleActiveOption = useCallback((id) => setActiveOptionId(id), []);
  const getAnchor = () => anchorRef.current?.querySelector('.MuiOutlinedInput-root') || anchorRef.current;

  // Multi (consulta manual, hasta 20): la lista sigue abierta para elegir varios
  // (✓ en los elegidos); se cierra con Escape o al salir. Simple (asignar): cierra.
  const pick = (rec) => {
    if (max === 1) setText('');
    if (list.some((r) => recordKey(r) === recordKey(rec))) return;
    onChange(max === 1 ? [rec] : [...list, rec].slice(0, max));
  };
  const remove = (rec) => onChange(list.filter((r) => recordKey(r) !== recordKey(rec)));

  return (
    <Box data-testid="lead-record-picker">
      <Typography sx={{ ...leadsSectionTitleSx, mb: 0.75 }}>
        {max > 1 ? `${label}${list.length ? ` · ${list.length}/${max}` : ''}` : label}
      </Typography>
      {list.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }} data-testid="lead-record-picker-selected">
          {list.map((rec, index) => {
            const text_ = describeLeadRecordFilter(rec);
            const invalid = errorIndex === index;
            return (
              <Chip
                key={recordKey(rec)}
                size="small"
                label={text_}
                title={text_}
                disabled={disabled}
                onDelete={() => remove(rec)}
                deleteIcon={<CloseIcon aria-label={`Quitar ${text_}`} />}
                data-invalid={invalid || undefined}
                sx={{ ...leadItemChipSx, minWidth: 0, ...(invalid ? { border: `1px solid ${LEADS_DANGER}` } : {}) }}
              />
            );
          })}
        </Box>
      )}
      {!full && (
        <TextField
          ref={anchorRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setDismissed(false); }}
          onFocus={() => setDismissed(false)}
          onBlur={() => setDismissed(true)}
          onKeyDown={(e) => {
            if (open && resultsRef.current?.handleKeyDown(e)) return;
            if (e.key === 'Escape' && open) {
              // Primero cierra la lista (no el diálogo); otro Escape borra el texto.
              e.preventDefault();
              e.stopPropagation();
              setDismissed(true);
            } else if (e.key === 'Escape' && text) {
              e.stopPropagation();
              setText('');
            }
          }}
          placeholder="Buscar lote o unidad, ej: Depto 14E"
          size="small"
          fullWidth
          autoFocus={autoFocus}
          disabled={disabled}
          InputProps={{ startAdornment: <LeadSearchAdornment /> }}
          inputProps={{
            'aria-label': 'Buscar registro',
            maxLength: 120,
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': open,
            'aria-controls': open ? listId : undefined,
            'aria-activedescendant': open && activeOptionId ? activeOptionId : undefined,
          }}
          sx={leadsTextFieldSx}
        />
      )}
      {!full && text.trim() ? (
        <LeadRecordSearchResults
          text={text}
          ownerUserId={ownerUserId}
          onPick={pick}
          hasActivity={false}
          pickAny
          pickPrefix="Elegir"
          emptyLabel="Sin resultados."
          floating
          open={open}
          anchorEl={getAnchor}
          width={getAnchor()?.clientWidth || undefined}
          listId={listId}
          pickedKeys={list.map(recordKey)}
          itemKinds={itemKinds}
          onActiveOptionChange={handleActiveOption}
          ref={resultsRef}
        />
      ) : null}
      {(error || hint) && (
        <Typography
          role={error ? 'alert' : undefined}
          sx={{ fontSize: '12px', mt: 0.75, color: error ? LEADS_DANGER : LEADS_TEXT_MUTED }}
        >
          {error || hint}
        </Typography>
      )}
    </Box>
  );
}
